import React, {
    useContext,
    useEffect,
    useRef,
    useState,
    useMemo,
    useCallback,
} from "react";
import {observer} from "mobx-react-lite";
import {Context} from "../../../../../index";
import InputUI from "../../../../UI/Input/Input";
import CheckBoxCustom from "../../../../UI/CheckBoxCustom/CheckBoxCustom";
import ButtonUI from "../../../../UI/ButtonUI/ButtonUI";
import InputMask from "react-input-mask";
import OptionCountry from "./RegistrationForm/OptionCountry";
import {phoneCountryArr} from "../../../../../utils/consts";
import {ReactComponent as ArrowIcon} from "../../../../../assets/icons/arrow_bottom.svg";
import clsx from "clsx";

const FIELDS = [
    {name: "email", type: "email", placeholder: "Email*"},
    {name: "firstName", type: "text", placeholder: "Имя*"},
    {name: "lastName", type: "text", placeholder: "Фамилию"},
    {name: "city", type: "text", placeholder: "Город"},
];

const CHECKBOXES = [
    {
        name: "consentEmail",
        required: false,
        label: "Даю согласие на получение информационных рассылок по EMAIL",
    },
    {
        name: "consentSms",
        required: false,
        label: "Даю согласие на получение информационных рассылок по SMS",
    },
    {
        name: "consentPersonalData",
        required: true,
        label: (
            <>
                Даю согласие на обработку своих персональных данных в
                соответствии с{" "}
                <a
                    rel="noreferrer"
                    target="_blank"
                    href="/personal/privacy-policy/"
                >
                    Политикой обработки персональных данных
                </a>
            </>
        ),
    },
];

const RegistrationStep = observer(
    ({codeLimitErrorText, controllStateClass, goToPhoneStep, onRegister, captchaNode}) => {
        const {user} = useContext(Context);
        const inputRef = useRef(null);
        const [searchStateShow, setSearchStateShow] = useState("unactive");
        const [isLoading, setIsLoading] = useState(false);
        const [formError, setFormError] = useState("");

        const savedData = user.getRegistrationFormData();
        const [phoneNumber, setPhoneNumber] = useState(savedData.phone || "");
        const [country, setCountry] = useState(savedData.country || null);

        const [maskState, setMaskState] = useState(() => {
            if (country) {
                const found = phoneCountryArr.find((c) => c.name === country);
                return found || phoneCountryArr[0];
            }
            return phoneCountryArr[0];
        });

        const [formData, setFormData] = useState({
            email: savedData.email || "",
            firstName: savedData.firstName || "",
            lastName: savedData.lastName || "",
            city: savedData.city || "",
            consentPersonalData: savedData.consentPersonalData || false,
            consentEmail: savedData.consentEmail || false,
            consentSms: savedData.consentSms || false,
        });

        const [errors, setErrors] = useState({
            email: "",
            firstName: "",
            lastName: "",
            city: "",
        });

        const [touched, setTouched] = useState({
            email: false,
            firstName: false,
            lastName: false,
            city: false,
        });

        useEffect(() => {
            user.setRegistrationFormData({
                phone: phoneNumber,
                country: country,
                ...formData,
            });
        }, [phoneNumber, country, formData, user]);

        useEffect(() => {
            if (user.stepModals !== "registrationForm") {
                user.resetRegistrationFormData();
            }
        }, [user.stepModals, user]);

        const validateEmail = (email) => {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!email) return "Email обязателен для заполнения";
            if (!emailRegex.test(email)) return "Введите корректный email";
            return "";
        };

        const validateFirstName = (name) => {
            if (!name.trim()) return "Имя обязательно для заполнения";
            return "";
        };

        const validateCity = (city) => {
            if (city.trim() && city.trim().length < 2) {
                return "Город должен содержать минимум 2 символа";
            }
            return "";
        };

        const isFormValid = useMemo(() => {
            const phoneDigits = phoneNumber.replace(/[^\d]/g, "");

            const conditions = [
                !!formData.email.trim() && validateEmail(formData.email) === "",
                !!formData.firstName.trim() &&
                    validateFirstName(formData.firstName) === "",
                phoneDigits.length === Number(maskState.number),
                formData.consentPersonalData === true,
            ];

            return conditions.every(Boolean);
        }, [formData, phoneNumber, maskState]);

        const getFieldError = useCallback((field, value) => {
            switch (field) {
                case "email":
                    return validateEmail(value);
                case "firstName":
                    return validateFirstName(value);
                case "city":
                    return validateCity(value);
                default:
                    return "";
            }
        }, []);

        const handleFieldChange = useCallback(
            (field) => (e) => {
                const value = e.target.value;
                setFormData((prev) => ({...prev, [field]: value}));
                if (touched[field]) {
                    setErrors((prev) => ({
                        ...prev,
                        [field]: getFieldError(field, value),
                    }));
                }
            },
            [touched, getFieldError]
        );

        const handleFieldBlur = useCallback(
            (field) => (e) => {
                const value = e.target.value;
                setTouched((prev) => ({...prev, [field]: true}));
                setErrors((prev) => ({
                    ...prev,
                    [field]: getFieldError(field, value),
                }));
            },
            [getFieldError]
        );

        const handleCheckboxChange = (field) => (checked) => {
            setFormData({
                ...formData,
                [field]: checked,
            });
        };

        const setTelephoneCheckButton = (number) => {
            const replacenumber = number.replace(/[^\d]/g, "");
            setPhoneNumber(number);
            user.setPhoneNumber(number);
            user.setGetMeCodeButton(
                Number(replacenumber.length) === Number(maskState.number)
                    ? "active"
                    : "unactive"
            );
        };

        const ToggleShowContry = () => {
            setSearchStateShow(
                searchStateShow === "unactive" ? "active" : "unactive"
            );
        };

        const changeModelInput = (object) => {
            setMaskState(object);
            setPhoneNumber("");
            ToggleShowContry();
            setCountry(object.name);
        };

        const getCountryCode = (mask) => {
            const match = mask.match(/\+(\d+)/);
            if (match && match[1]) {
                return `+${match[1]}`;
            }
            return mask.split(" ")[0];
        };

        const handleSubmit = async (e) => {
            e.preventDefault();

            setTouched({
                email: true,
                firstName: true,
                lastName: true,
                city: true,
            });

            const emailError = validateEmail(formData.email);
            const firstNameError = validateFirstName(formData.firstName);
            const cityError = validateCity(formData.city);

            setErrors({
                email: emailError,
                firstName: firstNameError,
                city: cityError,
            });

            setFormError("");

            if (!isFormValid) return;

            setIsLoading(true);

            try {
                const replacePhoneNumber = phoneNumber.replace(/[^\d]/g, "");

                const formDataToSend = {
                    telephone: replacePhoneNumber,
                    telephoneMasked: phoneNumber,
                    email: formData.email,
                    firstName: formData.firstName,
                    lastName: formData.lastName || "",
                    city: formData.city || "",
                    country: country || "Россия",
                    consentEmail: formData.consentEmail,
                    consentSms: formData.consentSms,
                    consentPersonalData: formData.consentPersonalData,
                };

                const result = await onRegister(formDataToSend);

                if (result.success) {
                } else {
                    setFormError(result.error);
                }
            } catch (error) {
                setFormError(
                    error.response?.data?.error || "Ошибка регистрации"
                );
            } finally {
                setIsLoading(false);
            }
        };

        const checkToggler = (e) => {
            if (e.target.closest(".phone_and_city_wrap") === null) {
                setSearchStateShow("unactive");
            }
        };

        const handleGoToPhoneStep = () => {
            user.resetRegistrationFormData();
            if (goToPhoneStep) {
                goToPhoneStep();
            }
        };

        useEffect(() => {
            if (
                user.stepModals === "registrationForm" &&
                user.openAuthModal === 1
            ) {
                inputRef.current?.focus();
            }
        }, [user.openAuthModal, user.stepModals]);

        const errorText = formError || codeLimitErrorText;

        return (
            <form
                onSubmit={handleSubmit}
                onClick={checkToggler}
                className={"registration_form " + controllStateClass}
            >
                <div className={"fields_registration"}>
                    <span className={"fields_registration_title"}>
                        Регистрация
                    </span>

                    <div className={"fields_registration_wrapper"}>
                        <div className={"registration_select_class"}>
                            <div className={"registration_fields"}>
                                <div className={"phone_and_city_wrap"}>
                                    <div
                                        onClick={ToggleShowContry}
                                        className={"selected_input_wrapper"}
                                    >
                                        <img
                                            className={
                                                "selected_input_wrapper__img"
                                            }
                                            src={maskState.imgPath}
                                            alt=""
                                        />
                                        <span className="country-name">
                                            {maskState.name}
                                        </span>
                                        <div
                                            className={clsx(
                                                "selected_input_wrapper-icon",
                                                {
                                                    "selected_input_wrapper-icon--active":
                                                        searchStateShow ===
                                                        "active",
                                                }
                                            )}
                                        >
                                            <ArrowIcon />
                                        </div>
                                    </div>
                                    <div
                                        className={
                                            "searchResults " + searchStateShow
                                        }
                                    >
                                        {phoneCountryArr.length ? (
                                            phoneCountryArr.map((country, name) => (
                                                <OptionCountry
                                                    onClick={() =>
                                                        changeModelInput(
                                                            country
                                                        )
                                                    }
                                                    key={name}
                                                    country={country}
                                                />
                                            ))
                                        ) : (
                                            <p>
                                                К сожалению, такой страны нет.
                                                Свяжитесь с менеджером.
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="phone-input-container">
                                    <span className="phone-label">Телефон</span>
                                    <InputMask
                                        className="phoneInput"
                                        placeholder={getCountryCode(
                                            maskState.placeholder
                                        )}
                                        mask={maskState.mask}
                                        value={phoneNumber}
                                        onChange={(e) =>
                                            setTelephoneCheckButton(
                                                e.target.value
                                            )
                                        }
                                        maskChar={null}
                                    >
                                        {(inputProps) => (
                                            <input
                                                {...inputProps}
                                                ref={inputRef}
                                                type="tel"
                                                disableUnderline
                                            />
                                        )}
                                    </InputMask>
                                </div>
                            </div>

                            {FIELDS.map((field) => (
                                <div
                                    key={field.name}
                                    className="registration-field"
                                >
                                    <InputUI
                                        type={field.type}
                                        value={formData[field.name]}
                                        onChange={handleFieldChange(field.name)}
                                        onBlur={handleFieldBlur(field.name)}
                                        placeholder={field.placeholder}
                                        className={`registration-input ${
                                            errors[field.name] &&
                                            touched[field.name]
                                                ? "error"
                                                : ""
                                        }`}
                                    />
                                    {errors[field.name] &&
                                        touched[field.name] && (
                                            <span className="registration-error registration-error--absolute">
                                                {errors[field.name]}
                                            </span>
                                        )}
                                </div>
                            ))}

                            <div className="registration-checkboxes">
                                {CHECKBOXES.map((checkbox) => (
                                    <CheckBoxCustom
                                        key={checkbox.name}
                                        checked={formData[checkbox.name]}
                                        onChange={handleCheckboxChange(
                                            checkbox.name
                                        )}
                                        required={checkbox.required}
                                    >
                                        {checkbox.label}
                                    </CheckBoxCustom>
                                ))}
                            </div>
                        </div>

                        <div className="registration-submit-wrapper">
                            <div
                                className={
                                    "select_politic_type_error select_politic_type_error--absolute"
                                }
                                dangerouslySetInnerHTML={{
                                    __html: errorText,
                                }}
                            />

                            <ButtonUI
                                className={"app_telephone_registration__button"}
                                type={"submit"}
                                text={
                                    isLoading
                                        ? "Регистрация..."
                                        : "Зарегистрироваться"
                                }
                                disabled={!isFormValid || isLoading}
                            />
                            {captchaNode}

                            <div className="auth_switch_link">
                                <span
                                    className="auth_switch_link__text"
                                    onClick={handleGoToPhoneStep}
                                >
                                    Войти в уже существующий аккаунт
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        );
    }
);

export default RegistrationStep;
